import axios from "axios";
import {
  GOOGLE_SHEETS_API_KEY,
  GOOGLE_SHEETS_ID,
  GOOGLE_SHEETS_NAME,
} from "../env";
import { GiftIdea, GoogleSheetsResponse } from "../types";

// Using environment variables for sensitive information
const SHEET_ID = GOOGLE_SHEETS_ID;
const API_KEY = GOOGLE_SHEETS_API_KEY;
// The correct sheet name from metadata
const SHEET_NAME = GOOGLE_SHEETS_NAME;

/**
 * Creates a Google search query URL for a gift idea
 * @param name The name of the gift idea
 * @returns A Google search URL
 */
const createSearchQuery = (name: string): string => {
  return `https://www.google.com/search?q=${encodeURIComponent(
    name + " gift"
  )}`;
};

/**
 * Parses a comma-separated string into an array of trimmed strings
 * @param value The comma-separated string to parse
 * @returns An array of trimmed strings
 */
const parseMultiSelect = (value: string): string[] => {
  if (!value) return [];

  // Handle different formats of multi-select values
  // Some might be comma-separated, some might use other delimiters
  let items: string[] = [];

  // Try comma as delimiter
  if (value.includes(",")) {
    items = value
      .split(",")
      .map((item) => item.trim())
      .filter((item) => item);
  }
  // Try newline as delimiter
  else if (value.includes("\n")) {
    items = value
      .split("\n")
      .map((item) => item.trim())
      .filter((item) => item);
  }
  // Try semicolon as delimiter
  else if (value.includes(";")) {
    items = value
      .split(";")
      .map((item) => item.trim())
      .filter((item) => item);
  }
  // Single value
  else {
    items = [value.trim()];
  }

  return items;
};

/**
 * Parses a price string into a standardized format
 * @param value The price string to parse
 * @returns A standardized price string
 */
const parsePrice = (value: string): string => {
  if (!value) return "";
  return value.trim();
};

/**
 * Parses a rating string into a number
 * @param value The rating string to parse
 * @returns A rating number or undefined
 */
const parseRating = (value: string): number | undefined => {
  if (!value) return undefined;

  // Try to parse as a number
  const rating = parseFloat(value);
  if (isNaN(rating)) return undefined;

  // Ensure rating is between 0 and 5
  return Math.min(Math.max(rating, 0), 5);
};

/**
 * Fetches all gift ideas from the data source
 * @returns An array of gift ideas
 */
export const fetchAllGiftIdeas = async (): Promise<GiftIdea[]> => {
  try {
    // Using the correct sheet name with proper URL encoding
    const encodedSheetName = encodeURIComponent(`${SHEET_NAME}!A:Z`);
    const apiUrl = `https://sheets.googleapis.com/v4/spreadsheets/${SHEET_ID}/values/${encodedSheetName}?key=${API_KEY}`;

    const response = await axios.get<GoogleSheetsResponse>(apiUrl);
    const rows = response.data.values || [];

    // Assuming the first row contains headers
    if (rows.length <= 1) {
      return [];
    }

    const headers = rows[0];

    // Map the data from the user's sheet to our gift idea format
    const giftIdeas: GiftIdea[] = rows.slice(1).map((row, index) => {
      try {
        // Create a gift idea object from the row data
        const idea: Record<string, string> = {};

        // Map each column to the appropriate field
        headers.forEach((header, idx) => {
          if (idx < row.length) {
            // Convert header to lowercase and replace spaces with underscores for consistent property names
            const key = header.toLowerCase().replace(/\s+/g, "_");
            idea[key] = row[idx] || "";
          }
        });

        const name =
          idea.gift_name || idea.name || idea.title || `Gift Idea ${index + 1}`;
        const searchQuery = createSearchQuery(name);

        // Parse occasions and recipients as arrays
        const occasions = parseMultiSelect(idea.occasion || "");
        const recipients = parseMultiSelect(
          idea.recipient || idea.for_whom || ""
        );

        // Find the rating field - it might have different names
        let ratingValue = "";
        for (const key of Object.keys(idea)) {
          if (
            key.includes("rating") ||
            key.includes("rate") ||
            key === "stars"
          ) {
            ratingValue = idea[key];
            break;
          }
        }
        const rating = parseRating(ratingValue);

        // Find the price field - it might have different names
        let priceValue = "";
        for (const key of Object.keys(idea)) {
          if (
            key.includes("price") ||
            key.includes("cost") ||
            key === "value"
          ) {
            priceValue = idea[key];
            break;
          }
        }
        const price = parsePrice(priceValue);

        // Find image URL - check multiple possible field names and formats
        let imageUrl = "";
        for (const key of Object.keys(idea)) {
          const value = idea[key];
          if (!value) continue;

          // Check if the key name suggests it's an image
          if (
            key.includes("image") ||
            key.includes("photo") ||
            key.includes("picture") ||
            key.includes("img") ||
            key.includes("thumbnail")
          ) {
            imageUrl = value;
            break;
          }

          // Check if the value looks like an image URL
          if (
            (value.includes("http") || value.includes("https")) &&
            (value.includes(".jpg") ||
              value.includes(".jpeg") ||
              value.includes(".png") ||
              value.includes(".gif") ||
              value.includes(".webp") ||
              value.includes("image"))
          ) {
            imageUrl = value;
            break;
          }
        }

        // Add default values for any missing fields
        return {
          id: index + 1,
          name,
          description: idea.description || "",
          price: price,
          occasion: occasions,
          recipient: recipients,
          category: idea.category || "",
          // Use the image URL from the sheet if available, otherwise use a gift emoji image
          image_url:
            imageUrl ||
            `https://em-content.zobj.net/source/apple/354/wrapped-gift_1f381.png`,
          // Use the provided link or a Google search query as fallback
          purchase_link: idea.link || idea.purchase_link || "",
          search_query: searchQuery,
          rating,
        };
      } catch (error) {
        // Return a default gift idea with error information
        return {
          id: index + 1,
          name: `Gift Idea ${index + 1}`,
          description: "This gift idea could not be processed",
          price: "",
          occasion: [],
          recipient: [],
          category: "",
          image_url: `https://em-content.zobj.net/source/apple/354/wrapped-gift_1f381.png`,
          purchase_link: "",
          search_query: createSearchQuery(`Gift Idea ${index + 1}`),
          rating: undefined,
        };
      }
    });

    return giftIdeas;
  } catch (error: any) {
    // Return empty array on error
    return [];
  }
};

/**
 * Fetches gift ideas filtered by occasion
 * @param occasion The occasion to filter by
 * @returns An array of gift ideas for the specified occasion
 */
export const fetchGiftIdeasByOccasion = async (
  occasion: string
): Promise<GiftIdea[]> => {
  try {
    const allIdeas = await fetchAllGiftIdeas();

    const filteredIdeas = allIdeas.filter((idea) => {
      // Check if the occasion array contains the requested occasion (case-insensitive)
      return idea.occasion.some(
        (occ) => occ.toLowerCase() === occasion.toLowerCase()
      );
    });

    return filteredIdeas;
  } catch (error) {
    return [];
  }
};

/**
 * Fetches all unique occasions from the gift ideas
 * @returns An array of unique occasions
 */
export const fetchAllOccasions = async (): Promise<string[]> => {
  try {
    const allIdeas = await fetchAllGiftIdeas();

    // Extract all occasions from the arrays
    let allOccasions: string[] = [];
    allIdeas.forEach((idea) => {
      if (idea.occasion && idea.occasion.length > 0) {
        allOccasions = [...allOccasions, ...idea.occasion];
      }
    });

    // Filter out empty values
    allOccasions = allOccasions.filter((occasion) => occasion);

    const occasionsSet = new Set(allOccasions);
    const uniqueOccasions = Array.from(occasionsSet);

    return uniqueOccasions;
  } catch (error) {
    return [];
  }
};

/**
 * Fetches all unique recipients from the gift ideas
 * @returns An array of unique recipients
 */
export const fetchAllRecipients = async (): Promise<string[]> => {
  try {
    const allIdeas = await fetchAllGiftIdeas();

    // Extract all recipients from the arrays
    let allRecipients: string[] = [];
    allIdeas.forEach((idea) => {
      if (idea.recipient && idea.recipient.length > 0) {
        allRecipients = [...allRecipients, ...idea.recipient];
      }
    });

    // Filter out empty values
    allRecipients = allRecipients.filter((recipient) => recipient);

    const recipientsSet = new Set(allRecipients);
    const uniqueRecipients = Array.from(recipientsSet);

    return uniqueRecipients;
  } catch (error) {
    return [];
  }
};

/**
 * Fetches all unique price ranges from the gift ideas
 * @returns An array of unique price ranges
 */
export const fetchAllPriceRanges = async (): Promise<string[]> => {
  try {
    const allIdeas = await fetchAllGiftIdeas();

    // Extract all price ranges
    const priceRanges = allIdeas
      .map((idea) => idea.price)
      .filter((price) => price);

    const priceRangesSet = new Set(priceRanges);
    const uniquePriceRanges = Array.from(priceRangesSet);

    return uniquePriceRanges;
  } catch (error) {
    return [];
  }
};
